/**
 * Utility functions for cable insertion and sorting
 */

/**
 * Funzione helper per confrontare siglature in modo intelligente
 * Gestisce sia parti numeriche che testuali per un ordinamento naturale
 */
export const compareSiglature = (a, b) => {
  const siglaturaA = (a || '').toLowerCase();
  const siglaturaB = (b || '').toLowerCase();
  
  // Estrai parti numeriche e testuali
  const extractParts = (str) => {
    const parts = [];
    let current = '';
    let isNumeric = false;
    
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const charIsNumeric = /\d/.test(char);
      
      if (i === 0) {
        isNumeric = charIsNumeric;
        current = char;
      } else if (charIsNumeric === isNumeric) {
        current += char;
      } else {
        parts.push({
          value: current,
          isNumeric: isNumeric
        });
        current = char;
        isNumeric = charIsNumeric;
      }
    }
    
    if (current) {
      parts.push({
        value: current,
        isNumeric: isNumeric
      });
    }
    
    return parts;
  };
  
  const partsA = extractParts(siglaturaA);
  const partsB = extractParts(siglaturaB);
  
  const maxLength = Math.max(partsA.length, partsB.length);
  
  for (let i = 0; i < maxLength; i++) {
    const partA = partsA[i] || { value: '', isNumeric: false };
    const partB = partsB[i] || { value: '', isNumeric: false };
    
    if (partA.isNumeric && partB.isNumeric) {
      const numA = parseInt(partA.value);
      const numB = parseInt(partB.value);
      if (numA !== numB) return numA - numB;
    } else {
      const comparison = partA.value.localeCompare(partB.value);
      if (comparison !== 0) return comparison;
    }
  }
  
  return 0;
};

/**
 * Funzione helper per inserire un cavo in ordine basato sulla siglatura
 * Gestisce sia cavi con gruppo che senza gruppo
 * 
 * @param {Object} newCable - Il cavo da inserire
 * @param {Array} existingCables - Array dei cavi esistenti
 * @param {Object} settings - Impostazioni dell'applicazione
 * @returns {Array} Array aggiornato dei cavi con il nuovo cavo inserito
 */
export const insertCableInOrder = (newCable, existingCables, settings) => {
  if (settings.marcaturePerCollegamento) {
    // In modalità collegamento, aggiungi alla fine
    return [...existingCables, { ...newCable, originalIndex: existingCables.length }];
  }

  // In modalità normale, inserisci in ordine per siglatura
  const siglatura = newCable.siglatura || '';
  const gruppo = newCable.gruppo || '';
  // L'originalIndex deve riflettere la posizione di inserimento, non sempre la lunghezza
  // Lo assegneremo dopo aver determinato l'insertIndex
  let newCableWithIndex = { ...newCable };
  
  // Se il cavo ha un gruppo, trova la posizione all'interno di quel gruppo
  if (gruppo && gruppo.trim() !== '') {
    // Trova l'inizio e la fine del gruppo nell'elenco
    let groupStartIndex = -1;
    let groupEndIndex = existingCables.length;
    
    for (let i = 0; i < existingCables.length; i++) {
      const existingCable = existingCables[i];
      const existingGruppo = existingCable.gruppo || '';
      
      if (existingGruppo === gruppo) {
        if (groupStartIndex === -1) {
          groupStartIndex = i; // Prima volta che troviamo il gruppo
        }
        groupEndIndex = i + 1; // Aggiorna la fine del gruppo
      } else if (groupStartIndex !== -1) {
        // Abbiamo già trovato il gruppo e ora siamo fuori, fermiamoci
        break;
      }
    }
    
    // Se il gruppo esiste, inserisci all'interno del gruppo
    if (groupStartIndex !== -1) {
      let insertIndex = groupEndIndex; // Default: alla fine del gruppo
      
      // Trova la posizione corretta all'interno del gruppo
      for (let i = groupStartIndex; i < groupEndIndex; i++) {
        const existingSiglatura = existingCables[i].siglatura || '';
        
        if (compareSiglature(siglatura, existingSiglatura) < 0) {
          insertIndex = i;
          break;
        }
      }
      
      // Inserisci il cavo nella posizione corretta con originalIndex coerente
      const updatedCables = [...existingCables];
      newCableWithIndex = { ...newCableWithIndex, originalIndex: insertIndex };
      updatedCables.splice(insertIndex, 0, newCableWithIndex);

      // Aggiorna gli originalIndex di tutti i cavi dopo l'inserimento
      for (let i = insertIndex + 1; i < updatedCables.length; i++) {
        updatedCables[i] = { ...updatedCables[i], originalIndex: i };
      }

      return updatedCables;
    }
  }
  
  // Se il cavo non ha gruppo o il gruppo non esiste, inserisci nell'elenco generale
  let insertIndex = existingCables.length;
  
  for (let i = 0; i < existingCables.length; i++) {
    const existingCable = existingCables[i];
    const existingSiglatura = existingCable.siglatura || '';
    const existingGruppo = existingCable.gruppo || '';
    
    // Se il cavo esistente ha un gruppo, salta (i cavi con gruppo vanno prima)
    if (existingGruppo && existingGruppo.trim() !== '') {
      continue;
    }
    
    // Usa il confronto intelligente
    if (compareSiglature(siglatura, existingSiglatura) < 0) {
      insertIndex = i;
      break;
    }
  }
  
  // Inserisci il cavo nella posizione corretta con originalIndex coerente
  const updatedCables = [...existingCables];
  newCableWithIndex = { ...newCableWithIndex, originalIndex: insertIndex };
  updatedCables.splice(insertIndex, 0, newCableWithIndex);

  // Aggiorna gli originalIndex di tutti i cavi dopo l'inserimento
  for (let i = insertIndex + 1; i < updatedCables.length; i++) {
    updatedCables[i] = { ...updatedCables[i], originalIndex: i };
  }

  return updatedCables;
};

/**
 * Funzione helper per validare i campi obbligatori di un cavo
 * 
 * @param {Object} cable - Il cavo da validare
 * @param {Array} existingCables - Array dei cavi esistenti (per controlli di duplicati)
 * @param {string} language - Lingua per i messaggi di errore
 * @returns {Array} Array dei campi mancanti
 */
export const validateCableFields = (cable, existingCables = [], language = 'it') => {
  const missingFields = [];
  
  // Validazioni specifiche per cavi standard
  if (!cable.poli || !Array.isArray(cable.poli)) {
    if (!cable.siglatura || cable.siglatura.trim() === '') {
      missingFields.push('siglatura');
    }
    if (!cable.colore || cable.colore.trim() === '') {
      missingFields.push('colore');
    }
    if (!cable.sezione || cable.sezione === 0) {
      missingFields.push('sezione');
    }
  }
  
  // Validazioni per cavi multipolari
  if (cable.poli && Array.isArray(cable.poli)) {
    if (!cable.nomeCavo || cable.nomeCavo.trim() === '') {
      missingFields.push('nome cavo');
    }
    if (cable.poli.length === 0) {
      missingFields.push('almeno un polo');
    }
  }
  
  return missingFields;
};
